import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Box, CircularProgress, Paper, IconButton, Tooltip, Checkbox, FormControlLabel, Button } from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { useNavigate } from 'react-router';
import Typography from '@mui/material/Typography';
import InfoIcon from '@mui/icons-material/Info';
import { LocalizationProvider } from '@mui/x-date-pickers';
import b64toBlob from 'b64-to-blob';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { RootState } from '../../../reducers';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { useWidgetContext } from '../../../hooks/widgetContext';
import { configureStackedBarChart } from '../../ChartConfiguration/ChartConfiguration';
import { fetchDocumentationTrackingSummary, exportDocumentationTrackingSummary, MenuProps } from '../WidgetService';
import DocumentationTrackingSummary from '../../../Models/Widget/DocumentationTrackingSummary.model';
import { TimePeriodFilterEnum } from '../../../Enum/TimePeriodFilterEnum';
import Authorization from '../../../utils/Authorization';
import { getDateFilter } from '../../HedisMetrics/HedisMetricService';
import TeamDropdown from '../../Shared/TeamDropdown';
import { fetchTeams } from '../../../store/careTeams.slice';
import '../Widgets.css';

const DocumentationTrackingSummaries = () => {
    const dispatch = useAppDispatch();
    const authData = new Authorization();
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const { activeProviders } = useSelector((state: RootState) => state.provider);
    const history = useNavigate();
    const {
        appointmentSelectedProvider,
        setAppointmentSelectedProvider,
        appointmentSelectedTeam,
        setAppointmentSelectedTeam,
        appointmentDateFilter,
        setAppointmentDateFilter,
        appointmentFromDate,
        setAppointmentFromDate,
        appointmentToDate,
        setAppointmentToDate,
        appointmentShowInActiveProviders,
        setAppointmentShowInActiveProviders,
        appointmentShowInActiveTeams,
        setAppointmentShowInActiveTeams,
    } = useWidgetContext();
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [isGraphShow, setIsGraphShow] = useState<boolean>(false);
    const [downLoading, setDownLoading] = useState<boolean>(false);
    const [documentSummary, setDocumentSummary] = useState<DocumentationTrackingSummary>();
    const [chartOptions, setChartOptions] = useState({});
    const [show, setShow] = useState(false);
    const ref = useRef(null);
    const [loadedPage, setLoadedPage] = useState(false);
    const filteredProviders = (appointmentShowInActiveProviders ? activeProviders : activeProviders.filter((p) => p.Role.RoleName !== 'Inactive')) || [];
    const chartHeight = 490;

    useEffect(() => {
        dispatch(fetchTeams(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClick = (event) => {
        setShow(!show);
    };

    const filterList = getDateFilter().filter((d) => d.Value !== 'Custom' && d.Value !== 'Current Month');

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }
        setChartOptions(
            configureStackedBarChart(
                'Documentation Tracking Summary',
                'Appointment Count',
                'Appointment',
                'Count',
                [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                [0],
                'Appointment',
                history,
                '',
                '',
                chartHeight
            )
        );

        setAppointmentSelectedTeam(authData.getDefaultTeamId(''));
        setIsFetched(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFetched, history, setAppointmentSelectedTeam]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        if (activeProviders?.length && teams?.length) {
            setLoadedPage(true);
        }
    }, [teams, activeProviders, loadedPage, authData.TeamId, setAppointmentSelectedTeam]);

    useEffect(() => {
        if (loadedPage && (appointmentDateFilter !== TimePeriodFilterEnum.Custom || (Boolean(appointmentFromDate) && Boolean(appointmentToDate)))) {
            const changeFilter = async () => {
                setIsGraphShow(false);
                const data = await fetchDocumentationTrackingSummary(
                    appointmentSelectedProvider,
                    appointmentSelectedTeam,
                    appointmentDateFilter,
                    Boolean(appointmentFromDate) && appointmentDateFilter === TimePeriodFilterEnum.Custom ? new Date(appointmentFromDate).toISOString() : '',
                    Boolean(appointmentToDate) && appointmentDateFilter === TimePeriodFilterEnum.Custom ? new Date(appointmentToDate).toISOString() : '',
                    appointmentShowInActiveTeams,
                    appointmentShowInActiveProviders,
                    false
                );
                setDocumentSummary(data);
            };
            setChartOptions(
                configureStackedBarChart(
                    'Documentation Tracking Summary',
                    'Appointment Count',
                    'Appointment',
                    'Count',
                    [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                    [0],
                    'Appointment',
                    history,
                    '',
                    '',
                    chartHeight
                )
            );
            changeFilter();
        }

        if (appointmentDateFilter !== TimePeriodFilterEnum.Custom && Boolean(appointmentFromDate) && Boolean(appointmentToDate)) {
            setAppointmentFromDate(null);
            setAppointmentToDate(null);
        }
    }, [
        loadedPage,
        appointmentDateFilter,
        appointmentFromDate,
        appointmentToDate,
        setAppointmentFromDate,
        setAppointmentToDate,
        history,
        appointmentSelectedTeam,
        appointmentSelectedProvider,
        appointmentShowInActiveTeams,
        appointmentShowInActiveProviders,
    ]);

    useEffect(() => {
        if (documentSummary) {
            let xAxisValues = [];
            let yAxisValues = [];
            let documentedWithin3DaysValues = [];
            let documentedValues = [];
            let notDocumentedValues = [];

            let documentedWithin3DaysPercentage =
                documentSummary.TotalAppointments > 0 ? (documentSummary.DocumentedWithin3DaysAppointmentsCount / documentSummary.TotalAppointments) * 100 : 0;
            let d = {
                y: documentSummary.DocumentedWithin3DaysAppointmentsCount,
                name: Math.round(documentedWithin3DaysPercentage),
            };
            documentedWithin3DaysValues.push(d);

            let documentedPercentage =
                documentSummary.TotalAppointments > 0 ? (documentSummary.DocumentedAppointmentsCount / documentSummary.TotalAppointments) * 100 : 0;
            let d2 = {
                y: documentSummary.DocumentedAppointmentsCount,
                name: Math.round(documentedPercentage),
            };
            documentedValues.push(d2);

            let notDocumentedPercentage =
                documentSummary.TotalAppointments > 0 ? (documentSummary.NotDocumentedAppointmentsCount / documentSummary.TotalAppointments) * 100 : 0;
            let d1 = {
                y: documentSummary.NotDocumentedAppointmentsCount,
                name: Math.round(notDocumentedPercentage),
            };
            notDocumentedValues.push(d1);
            xAxisValues.push('');

            yAxisValues.push({ name: 'Documented Within 3 Days', data: documentedWithin3DaysValues, color: '#4682B4', showInLegend: true });
            yAxisValues.push({ name: 'Documented', data: documentedValues, color: '#6495ED', showInLegend: true });
            yAxisValues.push({ name: 'Not Documented', data: notDocumentedValues, color: '#ff7d7d', showInLegend: true });

            if (documentSummary.DocumentedWithin3DaysAppointmentsCount === 0 && documentSummary.DocumentedAppointmentsCount === 0 && documentSummary.NotDocumentedAppointmentsCount === 0) {
                setChartOptions(
                    configureStackedBarChart(
                        'Documentation Tracking Summary',
                        'Appointment Count',
                        'Appointment',
                        'Count',
                        [{ name: '', data: [], showInLegend: false, title: { text: null } }],
                        [0],
                        'Appointment',
                        history,
                        '',
                        '',
                        chartHeight
                    )
                );
            } else {
                let subTitle = '',
                    xAxisTitle = '',
                    yAxisTitle = 'Count';
                setChartOptions(
                    configureStackedBarChart('Appointment Chart', subTitle, xAxisTitle, yAxisTitle, yAxisValues, xAxisValues, 'Appointment', history,
                        '',
                        '',
                        chartHeight)
                );
            }

            setIsGraphShow(true);
        }
    }, [documentSummary, history]);

    const changeFromDate = (date: any) => {
        setAppointmentFromDate(date);
        let dateInfo = new Date(date);
        if (!Boolean(appointmentToDate) || new Date(appointmentToDate) <= dateInfo) {
            let endDate = dateInfo.setDate(dateInfo.getDate() + 30);
            setAppointmentToDate(endDate);
        }
    };

    const changeToDate = (date: any) => {
        setAppointmentToDate(date);
        let dateInfo = new Date(date);
        if (!Boolean(appointmentFromDate) || new Date(appointmentFromDate) >= dateInfo) {
            let startDate = dateInfo.setDate(dateInfo.getDate() - 30);
            setAppointmentFromDate(startDate);
        }
    };

    const navigateToMembers = () => {
        history(`/provider/metricsummary/appointmentmemberdetail`);
    };

    const toggleProviderHandler = (isChecked: boolean) => {
        setAppointmentShowInActiveProviders(isChecked);
        setAppointmentSelectedProvider('All');
    };

    const exportDetail = async () => {
        setDownLoading(true);
        const data = await exportDocumentationTrackingSummary(
            appointmentSelectedProvider,
            appointmentSelectedTeam,
            appointmentDateFilter,
            Boolean(appointmentFromDate) && appointmentDateFilter === TimePeriodFilterEnum.Custom ? new Date(appointmentFromDate).toISOString() : '',
            Boolean(appointmentToDate) && appointmentDateFilter === TimePeriodFilterEnum.Custom ? new Date(appointmentToDate).toISOString() : '',
            appointmentShowInActiveTeams,
            appointmentShowInActiveProviders
        );

        let blob = b64toBlob(data.base64String, 'text/csv');
        let url = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        var date = new Date();
        var mm = date.getMonth() + 1; // getMonth() is zero-based
        var dd = date.getDate();
        var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
        link.setAttribute('download', `DocumentationTracking - ${dateInfo}.xlsx`);
        link.click();
        link.remove();

        setDownLoading(false);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid size={{ xs: 12, lg: 6 }}>
                <Paper className="smartdashboard-widget" elevation={5} sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                    <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                        <Grid size={12} textAlign="center">
                            <Typography display={'inline-block'} variant="h6" marginRight={1} color={'primary'} ref={ref}>
                                <span className="pointer" onClick={navigateToMembers}>
                                    Documentation Tracking Summary
                                </span>
                                <Tooltip
                                    open={show}
                                    title={
                                        <div>
                                            <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                Documented Within 3 Days :
                                            </Typography>
                                            <Typography variant="caption">
                                                Appointments that have been documented within three business days of the appointment date
                                            </Typography> <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                Documented :
                                            </Typography>
                                            <Typography variant="caption">
                                                Appointments that have been documented after three business days from the appointment date.
                                            </Typography>
                                            <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                Not Documented :
                                            </Typography>
                                            <Typography variant="caption">Documentation has not been completed yet</Typography>
                                        </div>
                                    }
                                    PopperProps={{ style: { zIndex: 1000 } }}
                                >
                                    <IconButton onClick={handleClick}>
                                        <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                    </IconButton>
                                </Tooltip>
                            </Typography>
                        </Grid>
                        <Grid size={3.2}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Providers</InputLabel>
                                <Select
                                    value={appointmentSelectedProvider}
                                    label="Providers"
                                    name="Providers"
                                    onChange={(e) => setAppointmentSelectedProvider(e.target.value)}
                                    MenuProps={MenuProps}
                                >
                                    <MenuItem key="All" value="All">
                                        All
                                    </MenuItem>
                                    <FormControlLabel
                                        sx={{ pl: '12px', mb: '0px' }}
                                        control={
                                            <Checkbox checked={appointmentShowInActiveProviders} onChange={(e) => toggleProviderHandler(e.target.checked)} />
                                        }
                                        label="Show Inactive"
                                    />
                                    {filteredProviders.map((pro) => (
                                        <MenuItem key={pro.Id} value={pro.Id} sx={{ color: pro.Role.RoleName === 'Inactive' ? 'grey' : 'black' }}>
                                            {pro.LastName}, {pro.FirstName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={3.2}>
                            <TeamDropdown
                                teams={teams}
                                selectedTeam={appointmentSelectedTeam}
                                onChange={(id) => setAppointmentSelectedTeam(id)}
                                showInactive={appointmentShowInActiveTeams}
                                onToggleInactive={setAppointmentShowInActiveTeams}
                            />
                        </Grid>
                        <Grid size={3.2}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Date Filter</InputLabel>
                                <Select
                                    value={appointmentDateFilter}
                                    label="Date Filter"
                                    name="Date"
                                    onChange={(e) => setAppointmentDateFilter(parseInt(e.target.value as any))}
                                >
                                    {filterList.map((date) => (
                                        <MenuItem key={date.Id} value={date.Id}>
                                            {date.Value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={2.4}>
                            <Button
                                style={{ minWidth: '120px', marginLeft: 'auto' }}
                                onClick={() => exportDetail()}
                                variant="contained"
                                color="success"
                                disabled={downLoading || (documentSummary?.DocumentedWithin3DaysAppointmentsCount === 0 && documentSummary?.DocumentedAppointmentsCount === 0 && documentSummary?.NotDocumentedAppointmentsCount === 0)}
                            >
                                {downLoading ? 'Exporting...' : 'Export'}
                            </Button>
                        </Grid>
                        {appointmentDateFilter === TimePeriodFilterEnum.Custom ? (
                            <React.Fragment>
                                <Grid size={{ md: 4 }}>
                                    <DesktopDatePicker
                                        label="From Date"
                                        value={Boolean(appointmentFromDate) ? appointmentFromDate : null}
                                        minDate={new Date('2017-01-01')}
                                        onChange={(newValue) => changeFromDate(newValue)}
                                        slotProps={{ textField: { fullWidth: true, size: "small" } }}
                                    />
                                </Grid>
                                <Grid size={{ md: 4 }}>
                                    <DesktopDatePicker
                                        label="To Date"
                                        value={Boolean(appointmentToDate) ? appointmentToDate : null}
                                        minDate={new Date('2017-01-01')}
                                        onChange={(newValue) => changeToDate(newValue)}
                                        slotProps={{ textField: { fullWidth: true, size: "small" } }}
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : null}
                        <Grid size={12} sx={{ position: 'relative' }}>
                            {!isGraphShow ? (
                                <Box className="loader-center">
                                    <CircularProgress />
                                </Box>
                            ) : null}
                            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        </LocalizationProvider>
    );
};

export default DocumentationTrackingSummaries;
